import {
  useSuperAdminShops,
  useSuperAdminShopsCatalog,
} from "@/api/super-admin/use-super-admin-shops";
import { useTrackingErrorsSummary } from "@/api/super-admin/use-tracking-queue-errors";
import { useRecentSuperAdminActions } from "@babackapp/private-api-client/client/use-super-admin-actions";
import {
  useMonitoringEvents,
  useMonitoringEventsSummary,
} from "@babackapp/private-api-client/client/use-super-admin-monitoring-events";
import { AlertTriangle, ArrowRight, BellRing, Store } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageShell } from "./admin-tab-pages";
import { categoryLabel } from "./monitoring-events-page";
import { formatRelative } from "./shop-shared";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
/** Same threshold as the backend `staleShopsCount`. */
const STATS_STALE_AFTER_MS = 2 * HOUR_MS;

type WatchReason =
  | "activity-drop"
  | "inactive-but-used"
  | "catalog-gap"
  | "stale-stats";

const WATCH_REASONS: Record<WatchReason, { label: string; hint: string }> = {
  "activity-drop": {
    label: "Activité en chute",
    hint: "Cliente active, des retours sur 30 jours mais aucun sur 7 jours",
  },
  "inactive-but-used": {
    label: "Désactivée mais utilisée",
    hint: "isActive = false alors que des retours arrivent cette semaine",
  },
  "catalog-gap": {
    label: "Catalogue incomplet",
    hint: "Moins de 90 % des produits Shopify chargés en base",
  },
  "stale-stats": {
    label: "Stats en retard",
    hint: "Stats absentes ou de plus de 2 h : le cron horaire l'a manquée",
  },
};

function percent(part: number, total: number): string {
  return total > 0 ? `${((part / total) * 100).toFixed(1)} %` : "—";
}

function Panel({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-background">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {icon}
          {title}
        </h2>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function SeeAll({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 hover:underline"
    >
      Tout voir <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

/**
 * What needs a look today, not the fleet table: health of the last 24 h,
 * shops to watch, newcomers and what the team changed.
 */
export function AdminDashboardPage() {
  const overview = useSuperAdminShops();
  const catalog = useSuperAdminShopsCatalog();
  const eventsSummary = useMonitoringEventsSummary({ hours: 24 });
  const lastErrors = useMonitoringEvents({ severity: "ERROR" });
  const carrierErrors = useTrackingErrorsSummary({ isResolved: false });
  const actions = useRecentSuperAdminActions(10);

  const shops = useMemo(() => overview.data?.shops ?? [], [overview.data]);
  // Frozen at mount: the lists below must not reshuffle on every render.
  const [now] = useState(() => Date.now());

  const fleet = useMemo(() => {
    const counted = shops.filter(
      (shop) => !shop.isTestShop && shop.isIncludedInStats !== false,
    );
    const sum = (key: keyof (typeof counted)[number]) =>
      counted.reduce((total, shop) => total + Number(shop[key] ?? 0), 0);
    return {
      returns24h: sum("returnRequestsLast24Hours"),
      orders24h: sum("ordersLast24Hours"),
      returns7d: sum("returnRequestsLast7Days"),
      orders7d: sum("ordersLast7Days"),
      returns30d: sum("returnRequestsLast30Days"),
      orders30d: sum("ordersLast30Days"),
      active7d: counted.filter((shop) => shop.returnRequestsLast7Days > 0)
        .length,
      shopsCount: counted.length,
    };
  }, [shops]);

  const watchList = useMemo(() => {
    const catalogByShop = new Map(
      (catalog.data?.catalogs ?? []).map((item) => [item.shopId, item]),
    );
    const rows: { id: string; name: string; reason: WatchReason }[] = [];
    for (const shop of shops) {
      if (shop.isTestShop) continue;
      if (
        shop.isActive !== false &&
        shop.clientStatus === "active" &&
        shop.returnRequestsLast30Days >= 10 &&
        shop.returnRequestsLast7Days === 0
      ) {
        rows.push({ id: shop.id, name: shop.name, reason: "activity-drop" });
      }
      if (shop.isActive === false && shop.returnRequestsLast7Days > 0) {
        rows.push({
          id: shop.id,
          name: shop.name,
          reason: "inactive-but-used",
        });
      }
      const item = catalogByShop.get(shop.id);
      const expected = item?.catalogProductCount ?? null;
      const loaded = item?.loadedCatalogProductCount ?? null;
      if (
        expected !== null &&
        loaded !== null &&
        expected >= 50 &&
        loaded < expected * 0.9
      ) {
        rows.push({ id: shop.id, name: shop.name, reason: "catalog-gap" });
      }
      if (
        shop.isActive !== false &&
        (shop.statsComputedAt === null ||
          shop.statsComputedAt === undefined ||
          shop.statsComputedAt < now - STATS_STALE_AFTER_MS)
      ) {
        rows.push({ id: shop.id, name: shop.name, reason: "stale-stats" });
      }
    }
    return rows;
  }, [shops, catalog.data, now]);

  const newcomers = useMemo(
    () =>
      shops
        .filter((shop) => shop.createdAt >= now - 30 * DAY_MS)
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 8),
    [shops, now],
  );

  const errors = (lastErrors.data?.pages[0]?.events ?? []).slice(0, 5);
  const openCarrierErrors = carrierErrors.data?.total ?? null;

  return (
    <AdminPageShell
      title="Dashboard"
      description="Ce qui demande un coup d'œil aujourd'hui. Le détail de chaque boutique est dans Boutiques."
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          {
            label: "Retours 24 h",
            value: fleet.returns24h,
            sub: `${percent(fleet.returns24h, fleet.orders24h)} des commandes`,
          },
          {
            label: "Retours 7 j",
            value: fleet.returns7d,
            sub: `${percent(fleet.returns7d, fleet.orders7d)} des commandes`,
          },
          {
            label: "Retours 30 j",
            value: fleet.returns30d,
            sub: `${percent(fleet.returns30d, fleet.orders30d)} des commandes`,
          },
          {
            label: "Boutiques actives 7 j",
            value: fleet.active7d,
            sub: `sur ${fleet.shopsCount} en production`,
          },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg border bg-background p-4">
            <div className="text-xs font-medium text-muted-foreground">
              {kpi.label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {overview.isPending ? "…" : kpi.value.toLocaleString("fr-FR")}
            </div>
            <div className="text-xs text-muted-foreground">{kpi.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel
          title="Santé, dernières 24 h"
          icon={<BellRing className="h-4 w-4 text-muted-foreground" />}
          action={<SeeAll to="/admin/events" />}
        >
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Link
              to="/admin/carrier-issues"
              className="rounded-md border p-3 hover:border-violet-400"
            >
              <div className="text-xs font-medium text-muted-foreground">
                Erreurs de suivi ouvertes
              </div>
              <div
                className={`mt-1 text-xl font-semibold tabular-nums ${openCarrierErrors ? "text-red-700" : "text-foreground"}`}
              >
                {openCarrierErrors === null
                  ? "…"
                  : openCarrierErrors.toLocaleString("fr-FR")}
              </div>
            </Link>
            {(eventsSummary.data?.categories ?? []).map((item) => (
              <Link
                key={item.category}
                to={`/admin/events?category=${item.category}`}
                className="rounded-md border p-3 hover:border-violet-400"
              >
                <div className="truncate text-xs font-medium text-muted-foreground">
                  {categoryLabel(item.category)}
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-xl font-semibold tabular-nums text-foreground">
                    {item.events}
                  </span>
                  {item.errors > 0 && (
                    <span className="text-xs font-medium text-red-700">
                      {item.errors} err.
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
          {eventsSummary.data?.categories.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              Aucun événement sur les dernières 24 h.
            </p>
          )}
          {errors.length > 0 && (
            <ul className="mt-4 divide-y border-t">
              {errors.map((alert) => (
                <li key={alert.id} className="flex items-start gap-2 py-2">
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-500"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground">
                      {alert.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatRelative(new Date(alert.createdAt).getTime())}
                      {alert.shopName && ` · ${alert.shopName}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Boutiques à surveiller"
          icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
        >
          {overview.isPending ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : watchList.length === 0 ? (
            <p className="text-sm text-muted-foreground">Rien à signaler.</p>
          ) : (
            <ul className="max-h-96 divide-y overflow-y-auto">
              {watchList.map((row) => (
                <li
                  key={`${row.id}-${row.reason}`}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <Link
                    to={`/admin/shops/${row.id}`}
                    className="truncate text-sm font-medium text-foreground hover:text-violet-700 hover:underline"
                  >
                    {row.name}
                  </Link>
                  <span
                    className="shrink-0 rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
                    title={WATCH_REASONS[row.reason].hint}
                  >
                    {WATCH_REASONS[row.reason].label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Nouvelles boutiques, 30 jours"
          icon={<Store className="h-4 w-4 text-muted-foreground" />}
          action={<SeeAll to="/admin/shops" />}
        >
          {newcomers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucune boutique créée ces 30 derniers jours.
            </p>
          ) : (
            <ul className="divide-y">
              {newcomers.map((shop) => (
                <li
                  key={shop.id}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/admin/shops/${shop.id}`}
                      className="block truncate text-sm font-medium text-foreground hover:text-violet-700 hover:underline"
                    >
                      {shop.name}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      créée {formatRelative(shop.createdAt)}
                      {shop.isTestShop && " · test"}
                    </span>
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {shop.totalReturnRequests.toLocaleString("fr-FR")} retours
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Dernières actions des super admins">
          {actions.isPending ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : (actions.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Aucune écriture ni export récents.
            </p>
          ) : (
            <ul className="divide-y">
              {(actions.data ?? []).map((action) => (
                <li key={action.id} className="py-2">
                  <p className="truncate text-sm text-foreground">
                    <span className="font-medium">{action.userEmail}</span>
                    {" · "}
                    <span className="font-mono text-xs">
                      {action.method} {action.route}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatRelative(new Date(action.createdAt).getTime())}
                    {" · "}
                    {action.kind === "EXPORT" ? "export" : "écriture"}
                    {" · "}
                    <Link
                      to={`/admin/shops/${action.shopId}`}
                      className="text-violet-700 hover:underline"
                    >
                      {action.shopName ?? action.shopId}
                    </Link>
                    {action.detail && ` · ${action.detail}`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </AdminPageShell>
  );
}
